import type { ModelName, MutationMethodForModel, ParamsForMethod, ReturnForMethod } from "../../types";
import {
  getObjectEntries,
  type InvalidateTellescopeModelQueriesArgs,
  type SetTellescopeModelQueriesDataArgs,
} from "../../utils";
import type { SideEffectConfigConstructor, SideEffectConfigContext } from "./types";
import { getMutationSideEffectUpdaters } from "./get-mutation-side-effect-updaters";

/**
 * Retrieves the configured filter constructor used to target queries when executing side effects from
 * both custom and common mutation side effect configs for a given model and mutation method.
 *
 * @template Model - The Tellescope model name (e.g., "users", "appointments")
 * @template Method - The mutation method for the model (e.g., "create_user", "update_appointment")
 *
 * @param args.model - The Tellescope model name
 * @param args.method - The mutation method to retrieve side effects for
 * @param [args.context] - Additional context (e.g., session) for side effect config constructors
 */
export const getMutationSideEffectFilters = <Model extends ModelName, Method extends MutationMethodForModel<Model>>({
  model,
  method,
  context,
}: {
  model: Model;
  method: Method;
  context?: SideEffectConfigContext;
}):
  | ((
      request: ParamsForMethod<Model, Method>,
      response?: ReturnForMethod<Model, Method>
    ) => InvalidateTellescopeModelQueriesArgs<Model>["methodFilters"])
  | undefined => {
  return (...args: Parameters<SideEffectConfigConstructor<Model, Method>>) => {
    const configConstructor = getMutationSideEffectUpdaters({ model, method });
    if (!configConstructor) return undefined;

    const config = configConstructor(args[0], args[1], context);
    if (!config) return undefined;
    const typedConfig = config as NonNullable<SetTellescopeModelQueriesDataArgs<Model>["methodConfigs"]>;
    const entries = getObjectEntries(typedConfig);
    return entries.reduce<NonNullable<InvalidateTellescopeModelQueriesArgs<Model>["methodFilters"]>>(
      (acc, [method, config]) => {
        if (!config) return acc;
        const { updater: _, options: __, ...rest } = config;
        acc[method] = rest;
        return acc;
      },
      {}
    );
  };
};
